import KeyboardIcon from "@mui/icons-material/Keyboard";
import { Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import Slide from "@mui/material/Slide";
import { alpha } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import i18n from "i18next";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useFileContext } from "../../../../context/FileContext";
import "../../../../styles/quill.scss";
import { GradientTypography } from "../../../../utils/gradientTypography";
import { ColumnCenteredBox, RowCenteredBox } from "../../../../utils/styledBox";
import CustomToolbar from "./CustomToolbar";
import QuillInitializer from "./QuillInitializer";
import ImageHandler from "./ImageHandler";
import TextActions from "./TextActions";
import KreaVerse from "./KreaVerse";
import React from "react"; // React.memo を使用するためにインポート

const TextEditorComponent = () => {
  // コンポーネント名を変更
  const { t } = useTranslation();
  const { fileUuid: rawFileUuid } = useParams();
  const fileUuid = rawFileUuid === undefined ? "new" : rawFileUuid;
  const {
    fileTitle: fileTitleMap,
    fileText: fileTextMap,
    selection: selectionMap,
    shouldReplace: shouldReplaceMap,
    thumbnailImage: thumbnailImageMap,
    publicStatus: publicStatusMap,
    setFileText: contextSetFileText,
    setFileTitle: contextSetFileTitle,
    setSaveTrigger: contextSetSaveTrigger,
    setSelection: contextSetSelection,
    setShouldReplace: contextSetShouldReplace,
    setUpdateThumbnail: contextSetUpdateThumbnail,
  } = useFileContext();
  const [quill, setQuill] = useState<Quill | null>(null);

  const fileTitle = fileTitleMap[fileUuid!];
  const fileText = fileTextMap[fileUuid!];
  const selection = selectionMap[fileUuid!];
  const shouldReplace = shouldReplaceMap[fileUuid!];
  const thumbnailImage = thumbnailImageMap[fileUuid!];

  const lang = i18n.language;
  const quillRef = useRef<Quill | null>(null);

  const handleQuillInitialized = useCallback(
    (quillInstance: Quill) => {
      setQuill(quillInstance);
      quillRef.current = quillInstance;
      // 初期化時に publicStatus を context から取得して savedPublicStatus に設定
      const currentPublicStatus = publicStatusMap[fileUuid!];
      if (currentPublicStatus !== undefined) {
        setSavedPublicStatus(currentPublicStatus);
      }
    },
    [fileUuid, publicStatusMap]
  );

  // プレビューURL用に保存済みの公開状態を保持
  const [savedPublicStatus, setSavedPublicStatus] = useState(0);
  // publicUuid は FileContext から取得するため、ここでの宣言は不要
  const [kreaVerseStatus, setKreaVerseStatus] = useState<"generating" | "waiting" | "ready">("ready"); // kreaVerseより前に宣言

  // QuillInitializerからのカスタムイベントをリッスン
  const handleQuillSave = useCallback(() => {
    contextSetSaveTrigger(true); // TextActions側でsaveTriggerを監視
  }, [contextSetSaveTrigger]);

  // テキスト長の取得・Heading適応時、Topに戻る前のスクロール位置の記憶
  useEffect(() => {
    if (!quill) return;

    let debounceTimer: NodeJS.Timeout;

    const debouncedSetFileText = (text: string) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        contextSetFileText({ uuid: fileUuid!, content: text });
      }, 500); // 500msのデバウンス時間
    };

    const handleTextChange = () => {
      const text = quill.getText() || "";
      debouncedSetFileText(text);
    };

    const handleSelectionChange = (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      range: any | null
    ) => {
      if (range === null) return;
      const text = quill.getText(range.index, range.length);
      contextSetSelection({
        uuid: fileUuid!,
        index: range.index,
        length: range.length,
        text: text,
      });
    };

    quill.on("text-change", handleTextChange);
    quill.on("selection-change", handleSelectionChange);

    document.addEventListener("quillSaveTrigger", handleQuillSave);

    return () => {
      quill.off("text-change", handleTextChange);
      quill.off("selection-change", handleSelectionChange);
      document.removeEventListener("quillSaveTrigger", handleQuillSave);
      clearTimeout(debounceTimer); // クリーンアップ時にタイマーをクリア
    };
  }, [quill, fileUuid, contextSetFileText, contextSetSelection, handleQuillSave]);

  useEffect(() => {
    contextSetUpdateThumbnail(false);
  }, [fileUuid, contextSetUpdateThumbnail]);

  // テキスト置換処理
  useEffect(() => {
    if (quill && selection && shouldReplace) {
      const { index, length, text } = selection;
      quill.deleteText(index, length);
      quill.insertText(index, text, "user");
      contextSetShouldReplace({ uuid: fileUuid!, shouldReplace: false });
    }
  }, [selection, quill, shouldReplace, contextSetShouldReplace]);

  const boxRef = useRef<HTMLDivElement>(null); // TextActions に渡すため残す

  return (
    <>
      <QuillInitializer
        fileUuid={fileUuid}
        lang={lang}
        setQuill={setQuill}
        quillRef={quillRef}
        onInitialized={handleQuillInitialized}
      />
      <ImageHandler quill={quill} fileUuid={fileUuid} />
      <TextActions
        quill={quill}
        fileUuid={fileUuid}
        fileTitle={fileTitle}
        thumbnailImage={thumbnailImage}
        savedPublicStatus={savedPublicStatus}
        setSavedPublicStatus={setSavedPublicStatus}
        boxRef={boxRef}
      />
      <KreaVerse
        quillRef={quillRef}
        fileUuid={fileUuid}
        kreaVerseStatus={kreaVerseStatus}
        setKreaVerseStatus={setKreaVerseStatus}
        t={t}
      />
      <Fade in={true} timeout={1000}>
        <Box sx={{ borderRadius: 2, height: "100%", position: "relative" }}>
          <script async src="https://platform.twitter.com/widgets.js"></script>
          {/* ロックするためのオーバーレイはTextActions側で表示。以下のFadeは不要 */}
          <Box
            ref={boxRef}
            sx={{
              borderRadius: 2,
              display: "flex",
              justifyContent: "center",
              position: "relative",
              height: "100%",
              overflowY: "auto",
            }}
          >
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", height: "100%" }}>
              {/* Editor */}
              <Box
                sx={{ backgroundColor: "background.paper", borderRadius: 2, width: "100%" }}
                display={"flex"}
                flexDirection={"column"}
              >
                <Box
                  flexDirection={"column"}
                  justifyContent={"center"}
                  alignItems={"center"}
                  display={"flex"}
                  gap={1}
                  sx={{}}
                >
                  <Box
                    sx={{
                      flexDirection: "column",
                      position: "sticky",
                      display: "flex",
                      mt: 2,
                      top: 16,
                      zIndex: 10,
                    }}
                  >
                    <CustomToolbar quill={quill} />
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                      <Tooltip
                        title={<span style={{ whiteSpace: "pre-line" }}>{t("textEditor.kreaVerse.description")}</span>}
                      >
                        <Slide in={true} timeout={1000} direction={"down"}>
                          <RowCenteredBox
                            sx={{
                              borderRadius: 1,
                              mt: 1,
                              px: 2,
                              py: 0.2,
                              width: "fit-content",
                              background: (theme) => alpha(theme.palette.background.custom1, 0.4),
                              backdropFilter: "blur(6px)",
                              cursor: "pointer",
                            }}
                          >
                            {kreaVerseStatus === "generating" ? (
                              <div>
                                <CircularProgress
                                  size={16}
                                  sx={{ mr: 1, color: (theme) => theme.palette.text.secondary }}
                                />
                                <GradientTypography variant={"button"}>
                                  {t("textEditor.kreaVerse.generating")}
                                </GradientTypography>
                              </div>
                            ) : (
                              <GradientTypography variant={"button"}>
                                {t(
                                  kreaVerseStatus === "ready"
                                    ? "textEditor.kreaVerse.ready"
                                    : "textEditor.kreaVerse.waiting"
                                )}
                              </GradientTypography>
                            )}
                          </RowCenteredBox>
                        </Slide>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* タイトル */}
                  <ColumnCenteredBox sx={{ maxWidth: 720 }}>
                    <TextField
                      variant={"standard"}
                      placeholder={"Title"}
                      autoComplete={"off"}
                      fullWidth
                      color={"primary"}
                      onChange={(e) => {
                        contextSetFileTitle({ uuid: fileUuid!, title: e.target.value });
                      }}
                      sx={{
                        overflow: "hidden",
                        px: 2,
                        mt: 4,
                      }}
                      inputProps={{
                        className: "title-input",
                      }}
                      value={fileTitle || ""}
                    />
                    {/*  thumbnail*/}
                    {thumbnailImage ? (
                      <Box
                        sx={{
                          position: "relative",
                          mt: 2,
                          width: "100%",
                          overflow: "hidden",
                          px: 2,
                          borderRadius: 2,
                        }}
                      >
                        <img
                          src={thumbnailImage}
                          alt="thumbnail"
                          style={{ width: "100%", height: "100%", borderRadius: 4 }}
                        />
                      </Box>
                    ) : null}
                  </ColumnCenteredBox>

                  <Box
                    id="editor"
                    sx={{
                      fontSize: "1em",
                      maxWidth: 720,
                      width: "100%",
                      minHeight: "calc(100vh - 204px)",
                      pb: 4,
                      ".ql-editor": {
                        overflowY: "unset",
                        paddingBottom: "70vh",
                      },
                    }}
                  ></Box>
                </Box>

                {/* 文字数カウント */}
                {fileText && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      position: "fixed",
                      bottom: 16,
                      zIndex: 1,
                      p: 1,
                      m: 2,
                      width: "fit-content",
                    }}
                  >
                    <KeyboardIcon sx={{ mr: 0.5, fontSize: 16 }} />
                    <Typography variant={"caption"}>{fileText.length - 1 || 0}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>
    </>
  );
};

export default React.memo(TextEditorComponent);
